import { baseElementType, elementToUrl } from '#base/Canvas/Canvas.js';
import { Spinner } from '#base/ui/Spinner.js';
import { useAjax } from '#base/util/ajax.js';
import { Artwork, Font, JerseyFont, MonogramFont, MonogramFontFamily } from '@c2s-npm/plumlib';
import styled from '@emotion/styled';
import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';

export const groupToElements = (raw) => {
  const group = !!raw.toJS ? raw.toJS() : raw;
  if (group.isGroup && Array.isArray(group.data)) {
    return group.data.map((d) => {
      const e = Object.assign({}, group, d);
      delete e.isGroup;
      return e;
    });
  } else {
    delete group.isGroup;
    return [group];
  }
};

export const Element = ({
  value,
  groupIndex = 0,
  renderer = DefaultElementRender,
  getAssetUrl = (id) => `/custom/assets/${id}`,
  getFileUrl = (id) => `/custom/designs/${id}`,
  ...props
}) => {
  const isGroup = !!value.isGroup || Array.isArray(value.data);
  const element = isGroup ? Object.assign({}, value, value.data[groupIndex]) : value;

  // loading is a trinary value
  const [loading, setLoading] = useState(!element.url ? true : null);
  const [{ data, processing, error }, setArgs] = useAjax();

  useEffect(() => {
    element.assetId && setArgs({ url: getAssetUrl(element.assetId) });
    element.fileId && setArgs({ url: getFileUrl(element.fileId) });
  }, [element.assetId, element.fileId]);

  const asset = useMemo(() => {
    const assetType = data?.assetType || data?.[0]?.assetType;

    if (!assetType) {
      return null;
    } else if (assetType === 'font') {
      const font = new Font(data.id, { name: data.id, file: data.fileUrl });
      font.load(() => setLoading(false));
      return font;
    } else if (assetType === 'monogram') {
      if (Array.isArray(data)) {
        const members = data.reduce((a, d) => {
          a[d.attributes.role] = new MonogramFont(d.id, { name: d.id, file: d.fileUrl });
          return a;
        }, {});
        const family = new MonogramFontFamily(data[0].attributes.family, members);
        family.load(() => setLoading(false));
        return family;
      } else {
        const font = new MonogramFont(data.id, { name: data.id, file: data.fileUrl });
        font.load(() => setLoading(false));
        return font;
      }
    } else if (assetType === 'jersey') {
      const font = new JerseyFont(data.id, { name: data.id, file: data.fileUrl });
      font.load(() => setLoading(false));
      return font;
    } else if (assetType === 'file') {
      const asset = new Artwork(data.id, { name: data.filename, file: data.url });
      setLoading(false);
      return asset;
    } else {
      const asset = new Artwork(data.id, { name: data.assetName, file: data.fileUrl });
      setLoading(false);
      return asset;
    }
  }, [data]);

  const url = useMemo(
    () => (!!asset ? elementToUrl(element, { [baseElementType(value.elementType)]: [asset] }) : element.url),
    [element, asset, value, loading]
  );

  const Renderer = renderer;
  return <Renderer {...{ loading: loading || processing, url, element, groupIndex, ...props }} />;
};

export const BasicElement = (props) => <Element {...props} renderer={BasicElementRender} />;

export const DefaultElementRender = ({
  loading,
  url,
  element,
  groupIndex,
  dataRenderer = DefaultElementDataRender,
}) => {
  const oob = isOoby(element, groupIndex);

  // hack
  const color = !oob && element.color === '#fff' ? '#ccc' : element.color || '#1f1f1f';

  return (
    <div
      className={classNames({
        't-px-2': true,
        't-border-1 t-border-red t-my-2 t-rounded t-bg-red-200': oob,
      })}>
      <div className='t-flex t-items-center t-gap-5'>
        <div
          className={classNames({
            't-text-center': true,
            't-w-[80px]': element.isGroup,
          })}>
          {!loading && (
            <ElementImg
              url={url}
              color={color}
              className={`t-mx-auto t-inline-block t-h-[30px] t-min-w-[30px] t-max-w-full`}
            />
          )}
          {loading && <Spinner />}
        </div>

        {dataRenderer({ element })}
      </div>
      {oob && (
        <div className={'t-flex t-items-center t-gap-2 t-pb-2 t-text-red-700'}>
          <i className={'fa fa-exclamation-triangle'} />
          <div>This element isn't fully within the design bounds, some clipping will occur.</div>
        </div>
      )}
    </div>
  );
};

export const BasicElementRender = ({ loading, element, url, ...props }) => {
  const color = element.color === '#fff' ? '#ccc' : element.color;
  return (
    <div {...props}>
      {!loading && (
        <ElementImg
          url={url}
          color={color}
          className={`t-mx-auto t-inline-block t-h-[30px] t-min-w-[30px] t-max-w-full`}
        />
      )}
      {loading && <Spinner />}
    </div>
  );
};

const ElementImg = styled.div`
  mask: url(${(props) => props.url}) no-repeat left;
  -webkit-mask: url(${(props) => props.url}) no-repeat left;
  mask-size: contain;
  -webkit-mask-size: contain;
  background-color: ${(props) => props.color};
  content: ' ';
`;

export const ElementOob = ({ element }) => {
  const oob = element?.meta?.oob;
  if (!oob) return null;
  return (
    <div className='t-my-2'>
      <div className={'t-flex t-items-center t-gap-2 t-text-red-700'}>
        <i className={'fa fa-exclamation-triangle'} />
        <div>
          This template has elements that are not fully within the design bounds, some clipping will occur.
        </div>
      </div>
    </div>
  );
};

export const isOoby = ({ meta }, i) => {
  if (!meta) return false;

  const isGroupElement = !!meta.gb && i !== null && typeof i !== 'undefined';
  let oob = isGroupElement ? meta.gb[i]?.oob : meta.oob;

  if (oob && meta.gb) {
    const oobCount = meta.gb.filter((g) => g.oob).length;
    const allOoby = oobCount === meta.gb.length || oobCount === 0;
    if (isGroupElement && allOoby) {
      oob = false;
    }
    if (!isGroupElement && !allOoby) {
      oob = false;
    }
  }

  return oob;
};

const extractValue = (e) => {
  switch (e.elementType) {
    case 'font':
      return [[e.isGroup ? 'Name' : 'Text', e.data]];
    case 'monogram':
      return [['Initials', e.data]];
    case 'title':
      return [
        ['Name', e.data],
        ['Title', e.decoration],
      ];
    case 'jersey':
      return [
        ['Name', e.data],
        ['Number', e.decoration],
      ];
    case 'file':
    case 'uploaded':
      return [['File', e.filename]];
    default:
      return null;
  }
};
const DefaultElementDataRender = ({ element }) => {
  const data = !!element && extractValue(element);
  if (!data) return null;

  return (
    <div className='t-inline-flex t-flex-col t-items-center t-gap-1 md:t-flex-row md:t-gap-5'>
      {data.map(([label, value], i) => (
        <div key={i} className='t-mb-0 t-inline-flex t-items-center t-gap-[5px] t-text-brand-black'>
          <span className='t-text-xs t-font-medium'>{label}:</span>
          <span className='t-text-xs t-font-bold'>{value}</span>
        </div>
      ))}
    </div>
  );
};
